import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getOpeningHours, getRestaurantDetails } from '../../api';
import {
  Box,
  Grid,
  Typography,
  Popover,
  Button,
  ThemeProvider,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TbUsers } from 'react-icons/tb';
import logogrey from '../../Assets/logo/logo-icon-grey.svg';
import Calender from '../../Main/Components/Common/Calendar';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import SEO from '../../utils/SEO';
import {
  Theme,
  ThemeTerrace,
  ThemeForno,
  ThemePrive,
  ThemeNok,
} from '../../Themes';
import {
  isForno_Color,
  isReisty_Color,
  isTerrace_Color,
  isPrive_Color,
  isNok_Color,
  daily_events,
  getWidgetOutlinedButtonSx,
  isMaisonPrive,
} from './WidgetConfig';
import LoadingState from './Components/LoadingState';
import ErrorState from './Components/ErrorState';
import HeaderSection from './Components/HeaderSection';
import GuestInput from './Components/GuestInput';
import DateSelector from './Components/DateSelector';
import FloorPlanSelector from './Components/FloorPlanSelector';
import TimeSlotGrid from './Components/TimeSlotGrid';
import ReservationSummary from './Components/ReservationSummary';
import WidgetHeader from './Components/widget-header';
import { useGetQuery } from '../../hooks/useQuery';
import TodayExperience from './TodayExperience';

const ReservationWidget = () => {
  const dateObject = new Date();
  const { name } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const toDate = dateObject.toISOString().split('T')[0];

  const [loading, setLoading] = useState(false);

  const [date, setDate] = useState(toDate);
  const [restaurant, setRestaurant] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openingHours, setOpeningHours] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [pointSelected, setPointSelected] = useState(null);
  const [guest, setGuest] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [fullyBooked, setFullyBooked] = useState(false);

  const [currentTheme, setCurrentTheme] = useState(Theme);
  const [currentColor, setCurrentColor] = useState(isReisty_Color);
  const [new_deposit, setNewDeposit] = useState(null);

  // =======Restaurant config========
  const EVENTFUL = name === 'mercury-lagos';
  const IS_TERRACE = name === 'the-terrace-lagos';
  const IS_FORNO = name === 'forno-lagos';
  const IS_SHIRO = name === 'shiro';
  const IS_HARDROCK = name === 'hard-rock-cafe';
  const IS_PRIVE = isMaisonPrive(name);
  const IS_NOK = name === 'nok-by-alara';

  useEffect(() => {
    if (IS_TERRACE) {
      setCurrentColor(isTerrace_Color);
      setCurrentTheme(ThemeTerrace);
    } else if (IS_FORNO) {
      setCurrentColor(isForno_Color);
      setCurrentTheme(ThemeForno);
    } else if (IS_PRIVE) {
      setCurrentColor(isPrive_Color);
      setCurrentTheme(ThemePrive);
    } else if (IS_NOK) {
      setCurrentColor(isNok_Color);
      setCurrentTheme(ThemeNok);
    } else {
      setCurrentColor(isReisty_Color);
      setCurrentTheme(Theme);
    }
  }, [name]);

  // const isReisty = name === "reisty-bistro";
  // =======Restaurant config========

  const open = Boolean(anchorEl);
  const pid = open ? 'simple-popover' : undefined;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAlert = (variant, message) => {
    enqueueSnackbar(message, { variant });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelectTime = (time, point, deposit) => {
    setSelectedTime(time);
    setPointSelected(point);
    setNewDeposit(deposit);
  };

  const resetSelectedTime = () => {
    setSelectedTime(null);
    setPointSelected(null);
    setNewDeposit(null);
  };

  const [noData, setNoData] = useState(false);

  const {
    data,
    isLoading: fetchingRestaurant,
    refetch,
  } = useGetQuery(['reservation', name], `/guestservice/restaurant/${name}`);

  useEffect(() => {
    if (!fetchingRestaurant) {
      if (data?.result?.length > 0) {
        setRestaurant(data?.result[0]);
        resetSelectedTime();
        setSelectedFloor(data?.result[0]?.FloorPlans[0]?.Id);
      } else {
        setNoData(true);
      }
    }
  }, [data]);

  const [deposit, setDeposit] = useState(0);

  const handleGetTime = async () => {
    setIsLoading(true);
    setOpeningHours(null);
    await getOpeningHours(restaurant?.RestaurantId, date, guest, selectedFloor)
      .then((res) => {
        setIsLoading(false);

        if (res?.data?.status) {
          setOpeningHours(res?.data?.result[0]?.GroupedTime);
          setDeposit(res?.data?.result[0]?.ReservationDeposit);
          if (res?.data?.success_message === 'Fully booked') {
            setFullyBooked(true);
          } else {
            setFullyBooked(false);
          }
        } else {
          setOpeningHours(null);
          if (res?.data?.error_message === 'Fully booked') {
            setFullyBooked(true);
          } else {
            setFullyBooked(false);
            // handleAlert('error', `${res?.data?.error_message}`);
          }
        }
      })
      .catch((err) => {
        resetSelectedTime();

        // handleAlert('error', `${err?.message}`);

        setIsLoading(false);
        setOpeningHours(null);
      });
  };

  useEffect(() => {
    setDate(toDate);
  }, []);

  useEffect(() => {
    if (restaurant?.RestaurantId && guest > 0) {
      handleGetTime();
    }
  }, [name, date, restaurant, guest, selectedFloor]);

  const reservationDeposit = new_deposit?.Amount ?? deposit;

  const guestUrl = `/${name}/reservation/guest-booking?PartySize=${guest}&time=${selectedTime}&ReservationDay=${date}&deposit=${
    reservationDeposit
  }&floor=${selectedFloor}&pointValue=${
    pointSelected?.Value || null
  }&point_id=${pointSelected?.Id || null}&charges=${new_deposit?.ExtraCharge}&`;

  const userUrl = `/reservation/user?restaurantName=${name}&guest=${guest}&time=${selectedTime}&date=${date}&deposit=${
    reservationDeposit
  }&floor=${selectedFloor}&pointValue=${
    pointSelected?.Value || null
  }&point_id=${pointSelected?.Id || null}&charges=${new_deposit?.ExtraCharge}&`;

  const selectedFloorPlanName = restaurant?.FloorPlans?.find(
    (f) => f.Id === selectedFloor,
  )?.Name;
  const reservationDateLabel = date
    ? new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '--';

  useEffect(() => {
    resetSelectedTime();
  }, [guest, date]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const cardSurface = currentColor.surface ?? '#fff';
  const cardBorder = currentColor.border ?? '#edf0f3';
  const mutedText = currentColor.textMuted ?? '#777';
  const labelText = currentColor.text ?? '#3f3f46';
  const outlinedGuestSx = getWidgetOutlinedButtonSx(currentColor);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <SEO
        title={`${name} | Make a Reservation`}
        name="Reisty"
        type="article"
      />
      <ThemeProvider theme={currentTheme}>
        <WidgetHeader activeStep={activeStep} logo={restaurant?.Logo} />

        <Box
          className="hide_scrollbar"
          sx={{
            backgroundColor: currentColor.background,
            minHeight: '100vh',
            backgroundSize: 'contain',
            boxSizing: 'border-box',

            width: '100vw',
          }}
        >
          <Box
            className="hide_scrollbar"
            sx={{
              margin: '0 auto',
              width: { xl: '80%', lg: '80%', md: '85%', sm: '65%', xs: '100%' },
              bgcolor: currentColor.mainbg,
              minHeight: '100vh',
              boxSizing: 'border-box',
              pb: 4,
              overflow: 'visible',
            }}
          >
            <Toolbar />
            {fetchingRestaurant ? (
              <LoadingState currentColor={currentColor} />
            ) : (
              <>
                {noData ? (
                  <ErrorState
                    restaurant={restaurant}
                    name={name}
                    onRetry={refetch}
                    IS_TERRACE={IS_TERRACE}
                    IS_FORNO={IS_FORNO}
                    IS_SHIRO={IS_SHIRO}
                    IS_HARDROCK={IS_HARDROCK}
                  />
                ) : (
                  <Box
                    sx={{
                      maxWidth: 1100,
                      mx: 'auto',
                      px: { xs: 1, sm: 3 },
                      py: { xs: 3, md: 5 },
                      overflow: 'visible',
                    }}
                  >
                    <Grid
                      container
                      spacing={{ xs: 3, md: 4 }}
                      alignItems="flex-start"
                      sx={{ overflow: 'visible' }}
                    >
                      <Grid
                        item
                        xs={12}
                        md={5}
                        sx={{
                          position: { xs: 'static', md: 'sticky' },
                          top: { md: 56 },
                          alignSelf: 'flex-start',
                          height: 'fit-content',
                          zIndex: 2,
                        }}
                      >
                        <HeaderSection restaurant={restaurant} />
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Box
                          sx={{
                            bgcolor: cardSurface,
                            border: '1px solid',
                            borderColor: cardBorder,
                            borderRadius: '18px',
                            p: { xs: 2.5, sm: 3 },
                            pb: { xs: '240px', md: 3 },
                            boxShadow: IS_PRIVE
                              ? 'none'
                              : '0 18px 45px rgba(16, 24, 40, 0.08)',
                          }}
                        >
                          {activeStep === 0 && (
                            <>
                              <Typography
                                sx={{
                                  fontSize: { xs: '24px', md: '28px' },
                                  fontWeight: 900,
                                  lineHeight: 1.1,
                                  color: 'text.primary',
                                }}
                              >
                                Select Date & Time
                              </Typography>
                              <Typography
                                sx={{
                                  color: mutedText,
                                  fontSize: '13px',
                                  mt: 0.7,
                                }}
                              >
                                Choose your preferred dining experience
                              </Typography>

                              <div className="flex items-center gap-0 md:gap-2 flex-col md:flex-row">
                                <Box sx={{ mt: 3, flex: 1, width: '100%' }}>
                                  <GuestInput
                                    guest={guest}
                                    setGuest={setGuest}
                                    isLoading={isLoading}
                                    IS_FORNO={IS_FORNO}
                                  />
                                </Box>

                                <Box sx={{ mt: 3, flex: 1, width: '100%' }}>
                                  <DateSelector
                                    date={date}
                                    onClick={handleClick}
                                    pid={pid}
                                  />
                                </Box>
                              </div>

                              <Box sx={{ mt: 3 }}>
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 900,
                                    color: labelText,
                                  }}
                                >
                                  Choose Seating Area
                                </Typography>
                                <FloorPlanSelector
                                  floorPlans={restaurant?.FloorPlans}
                                  selectedFloor={selectedFloor}
                                  onSelectFloor={(floorId) => {
                                    setSelectedFloor(floorId);
                                    resetSelectedTime();
                                  }}
                                  currentColor={currentColor}
                                  IS_TERRACE={IS_TERRACE}
                                  isLoading={isLoading}
                                  photos={restaurant?.Photos}
                                />
                              </Box>

                              <Box sx={{ mt: 3 }}>
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 900,
                                    color: labelText,
                                  }}
                                >
                                  Select Time
                                </Typography>
                                <TimeSlotGrid
                                  IS_SHIRO={IS_SHIRO}
                                  events={restaurant?.Events}
                                  date={date}
                                  openingHours={openingHours}
                                  isLoading={isLoading}
                                  fullyBooked={fullyBooked}
                                  selectedTime={selectedTime}
                                  onSelectTime={handleSelectTime}
                                  currentColor={currentColor}
                                  guest={guest}
                                  IS_FORNO={IS_FORNO}
                                  selectedFloorPlanName={selectedFloorPlanName}
                                />
                              </Box>

                              {!isMobile && restaurant?.MenuLink && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    textAlign: 'right',
                                  }}
                                >
                                  <a
                                    href={restaurant?.MenuLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary optima underline"
                                    style={{
                                      fontSize: '13px',
                                      fontWeight: 800,
                                    }}
                                  >
                                    View Menu
                                  </a>
                                </Box>
                              )}

                              {isMobile && (
                                <>
                                  {restaurant?.Events?.length > 0 && (
                                    <>
                                      <Box
                                        sx={{
                                          mt: 3,
                                          display: {
                                            md: 'hidden',
                                            xs: 'block',
                                          },
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 800,
                                            fontSize: '15px',
                                          }}
                                        >
                                          Special Events
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: mutedText,
                                            fontSize: '12px',
                                            mt: 1,
                                          }}
                                        >
                                          Join one of our exclusive dining
                                          experiences
                                        </Typography>
                                        <div>
                                          <TodayExperience
                                            events={restaurant?.Events}
                                            date={date}
                                          />
                                        </div>
                                      </Box>
                                    </>
                                  )}
                                </>
                              )}

                              {EVENTFUL && (
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Box>
                                    {daily_events.map((event, index) => {
                                      const matching_day =
                                        event.day ===
                                        formatDate(date).split(',')[0];

                                      return (
                                        <Box
                                          key={index}
                                          sx={{
                                            background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('${
                                              restaurant?.Photos &&
                                              restaurant?.Photos[0]?.Link
                                            }')`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            mt: 1,
                                            display: matching_day
                                              ? 'flex'
                                              : 'none',
                                            p: 2,
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontWeight: 300,
                                              color: '#fff',
                                              fontSize: '12px',
                                            }}
                                          >
                                            Today's Event
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontWeight: 700,
                                              fontFamily: 'optima',
                                              color: '#fff',
                                              fontSize: '16px',
                                            }}
                                          >
                                            {event.name}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Grid>
                              )}

                              <Box
                                sx={{
                                  mt: 3,
                                  display: { xs: 'none', md: 'block' },
                                }}
                              >
                                <Button
                                  variant="contained"
                                  fullWidth
                                  sx={{
                                    borderRadius: '10px',
                                    py: 1.5,
                                    fontSize: '14px',
                                    fontWeight: 900,
                                  }}
                                  disabled={
                                    !guest ||
                                    !date ||
                                    !selectedTime ||
                                    isLoading
                                  }
                                  onClick={() => {
                                    if (isAuthenticated) {
                                      navigate(
                                        fullyBooked
                                          ? `${userUrl}waitlist=true`
                                          : `${userUrl}waitlist=false`,
                                      );
                                    } else {
                                      navigate('/login', {
                                        state: {
                                          redirectTo: fullyBooked
                                            ? `${userUrl}waitlist=true`
                                            : `${userUrl}waitlist=false`,
                                        },
                                      });
                                    }
                                  }}
                                >
                                  {isAuthenticated
                                    ? 'Reserve Table'
                                    : 'Login with Reisty'}
                                </Button>
                                {!isAuthenticated && (
                                  <>
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        color: mutedText,
                                        fontSize: '12px',
                                        my: 1.5,
                                      }}
                                    >
                                      or
                                    </Typography>
                                    <Button
                                      variant="outlined"
                                      fullWidth
                                      sx={{
                                        borderRadius: '10px',
                                        py: 1.4,
                                        fontSize: '14px',
                                        fontWeight: 900,
                                        ...outlinedGuestSx,
                                      }}
                                      disabled={
                                        !guest ||
                                        !date ||
                                        !selectedTime ||
                                        isLoading
                                      }
                                      onClick={() => {
                                        navigate(
                                          fullyBooked
                                            ? `${guestUrl}waitlist=true`
                                            : `${guestUrl}waitlist=false`,
                                        );
                                      }}
                                    >
                                      Continue as Guest
                                    </Button>
                                  </>
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: { xs: 'block', md: 'none' },
                                  mt: 5,
                                  zIndex: 1300,
                                  px: 2,
                                  pb: 'calc(16px + env(safe-area-inset-bottom))',
                                  pt: 1.5,
                                  bgcolor: cardSurface,
                                  borderTop: '1px solid',
                                  borderColor: cardBorder,
                                  boxShadow:
                                    '0 -12px 32px rgba(15, 23, 42, 0.08)',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    maxWidth: 1100,
                                    mx: 'auto',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                      mb: 2,
                                      gap: 2,
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: '12px',
                                          fontWeight: 800,
                                          textTransform: 'uppercase',
                                          color: mutedText,
                                          letterSpacing: '0.03em',
                                          fontFamily: 'optima',
                                        }}
                                        className="optima"
                                      >
                                        Your Reservation
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          flexWrap: 'wrap',
                                          columnGap: 1.2,
                                          rowGap: 0.5,
                                          mt: 0.75,
                                          color: mutedText,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            columnGap: 0.75,
                                          }}
                                        >
                                          <TbUsers
                                            style={{ fontSize: '16px' }}
                                          />
                                          <Typography
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: '14px',
                                            }}
                                          >
                                            {guest || '-'}
                                          </Typography>
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            color: '#8b8b96',
                                            fontSize: '8px',
                                          }}
                                        >
                                          •
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: '12px',
                                          }}
                                        >
                                          {reservationDateLabel || '--'}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            color: '#8b8b96',
                                            fontSize: '8px',
                                          }}
                                        >
                                          •
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            columnGap: 0.75,
                                            color: 'primary.main',
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontWeight: 800,
                                              fontSize: '14px',
                                            }}
                                          >
                                            {selectedTime || '--'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {restaurant?.MenuLink && (
                                      <a
                                        href={restaurant?.MenuLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary optima underline"
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: 700,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        View Menu
                                      </a>
                                    )}
                                  </Box>

                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      sx={{
                                        borderRadius: '10px',
                                        py: 1.5,
                                        fontSize: '14px',
                                        fontWeight: 900,
                                      }}
                                      disabled={
                                        !guest ||
                                        !date ||
                                        !selectedTime ||
                                        isLoading
                                      }
                                      onClick={() => {
                                        if (isAuthenticated) {
                                          navigate(
                                            fullyBooked
                                              ? `${userUrl}waitlist=true`
                                              : `${userUrl}waitlist=false`,
                                          );
                                        } else {
                                          navigate('/login', {
                                            state: {
                                              redirectTo: fullyBooked
                                                ? `${userUrl}waitlist=true`
                                                : `${userUrl}waitlist=false`,
                                            },
                                          });
                                        }
                                      }}
                                    >
                                      {isAuthenticated
                                        ? 'Reserve Table'
                                        : 'Login with Reisty'}
                                    </Button>
                                    {!isAuthenticated && (
                                      <>
                                        <Typography
                                          sx={{
                                            textAlign: 'center',
                                            color: mutedText,
                                            fontSize: '12px',
                                            my: 1.5,
                                          }}
                                        >
                                          or
                                        </Typography>
                                        <Button
                                          variant="outlined"
                                          fullWidth
                                          sx={{
                                            borderRadius: '10px',
                                            py: 1.4,
                                            fontSize: '14px',
                                            fontWeight: 900,
                                            ...outlinedGuestSx,
                                          }}
                                          disabled={
                                            !guest ||
                                            !date ||
                                            !selectedTime ||
                                            isLoading
                                          }
                                          onClick={() => {
                                            navigate(
                                              fullyBooked
                                                ? `${guestUrl}waitlist=true`
                                                : `${guestUrl}waitlist=false`,
                                            );
                                          }}
                                        >
                                          Continue as Guest
                                        </Button>
                                      </>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </>
                          )}
                          {activeStep === 1 && (
                            <ReservationSummary
                              restaurant={restaurant}
                              date={date}
                              guest={guest}
                              selectedTime={selectedTime}
                              onBack={() => setActiveStep(0)}
                              userUrl={userUrl}
                              guestUrl={guestUrl}
                              fullyBooked={fullyBooked}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            )}
            {IS_TERRACE && (
              <Typography sx={{ textAlign: 'center', mt: 4, color: '#b8b3a5' }}>
                Powered by <img src={logogrey} width={25} /> Reisty
              </Typography>
            )}
          </Box>
        </Box>

        <Popover
          id={pid}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box>
            <Calender
              future={false}
              past={true}
              onChildValue={(value) => {
                setDate(value);
                setAnchorEl(null);
              }}
            />
          </Box>
        </Popover>
      </ThemeProvider>
    </>
  );
};

export default ReservationWidget;
